import { Media } from "@ameelio/ui";
import ChatOutlined from "@mui/icons-material/ChatOutlined";
import MicOffOutlined from "@mui/icons-material/MicOffOutlined";
import ScreenShareOutlined from "@mui/icons-material/ScreenShareOutlined";
import Security from "@mui/icons-material/Security";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { Box, Card, Stack, Typography } from "@mui/material";
import { SystemStyleObject, Theme } from "@mui/system";
import { useTranslation } from "react-i18next";

type Props = {
  isHost: boolean;
  sx?: SystemStyleObject<Theme>;
  textColor: string;
};

export function WebinarHostInformation({ textColor }: { textColor: string }) {
  const { t } = useTranslation();
  return (
    <Stack spacing={1.5}>
      <Media gap={2} image={<ScreenShareOutlined />}>
        <Typography variant="body2" sx={{ color: textColor }}>
          {t(
            "Host(s) can interact with attendees, but attendees cannot see each other."
          )}
        </Typography>
      </Media>
      <Media gap={2} image={<MicOffOutlined />}>
        <Typography variant="body2" sx={{ color: textColor }}>
          {t(
            "Attendees are muted by default. Only hosts can unmute attendees."
          )}
        </Typography>
      </Media>
      <Media gap={2} image={<ChatOutlined />}>
        <Typography variant="body2" sx={{ color: textColor }}>
          {t(
            "Attendees can ask questions or raise hands. Only hosts can see this."
          )}
        </Typography>
      </Media>
      <Media gap={2} image={<Security />}>
        <Typography variant="body2" sx={{ color: textColor }}>
          {t(
            "COs maintain the right to monitor webinars. Host(s) are notified if one is doing so."
          )}
        </Typography>
      </Media>
    </Stack>
  );
}

function WebinarStudentInformation({ textColor }: { textColor: string }) {
  const { t } = useTranslation();
  return (
    <Stack spacing={1.5}>
      <Media gap={2} image={<VisibilityOutlined />}>
        <Typography variant="body2" sx={{ color: textColor }}>
          {t("Your video stream is only visible to the hosts")}
        </Typography>
      </Media>
      <Media gap={2} image={<MicOffOutlined />}>
        <Typography variant="body2" sx={{ color: textColor }}>
          {t(
            "Attendees are muted by default. Only hosts can unmute attendees."
          )}
        </Typography>
      </Media>
      <Media gap={2} image={<ChatOutlined />}>
        <Typography variant="body2" sx={{ color: textColor }}>
          {t("Participants can ask questions to the host or raise their hand")}
        </Typography>
      </Media>
      <Media gap={2} image={<Security />}>
        <Typography variant="body2" sx={{ color: textColor }}>
          {t("COs maintain the right to monitor webinars")}
        </Typography>
      </Media>
    </Stack>
  );
}

export default function WebinarInfoCard({ isHost, sx, textColor }: Props) {
  const { t } = useTranslation();
  return (
    <Card variant="outlined" sx={sx || {}}>
      <Stack
        spacing={1.5}
        sx={{
          py: 2,
          px: 2.5,
        }}
      >
        <Box>
          <Typography variant="body1" sx={{ color: textColor }}>
            {t("About webinars")}
          </Typography>
        </Box>
        {isHost ? (
          <WebinarHostInformation textColor={textColor} />
        ) : (
          <WebinarStudentInformation textColor={textColor} />
        )}
      </Stack>
    </Card>
  );
}
